import React from 'react';
import { Grid, Box, Typography, Checkbox, IconButton, Chip } from '@mui/material';
import {
  Edit,
  Delete,
  Article as ArticleIcon,
  AttachMoney,
  ThumbUp,
  Visibility,
  LocalOffer as TagIcon,
  FolderSpecial as CollectionIcon
} from '@mui/icons-material';
import CollectionCoverCard from '../CollectionCoverCard';

const CollectionCoverGrid = ({
  collections,
  variant = 'grid',
  onCollectionClick,
  onEditCollection,
  onDeleteCollection,
  selectionMode = false,
  selectedCollections = [],
  onCollectionSelect,
}) => {
  // Formatadores
  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    if (num >= 1000000000) return (num / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B';
    if (num >= 1000000) return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return num.toString();
  };

  const formatCurrency = (value) => {
    if (!value || value === 0) return 'R$ 0';
    return 'R$ ' + new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Renderização em lista
  if (variant === 'list') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {collections.map((collection) => {
          const isSelected = selectedCollections.includes(collection._id);
          const articleCount = Number(collection.articleCount ?? 0);
          const totalValuation = Number(collection.totalValuation ?? 0);
          const totalLikes = Number(collection.totalLikes ?? 0);
          const totalViews = Number(collection.totalViews ?? 0);
          const isTagBased = collection.type === 'tags';
          const tagCount = collection.tags?.length || 0;
          const createdAt = collection.createdAt ? new Date(collection.createdAt).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          }) : '';

          return (
            <Box
              key={collection._id || collection.id}
              onClick={() => onCollectionClick?.(collection)}
              sx={{
                display: 'flex',
                height: 140,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                '&:hover': {
                  bgcolor: isSelected ? 'action.selected' : 'action.hover',
                  borderColor: 'primary.main',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              {/* Checkbox de seleção */}
              {selectionMode && (
                <Box sx={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 10
                }}>
                  <Checkbox
                    checked={isSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      onCollectionSelect?.(collection._id);
                    }}
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.5)',
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                    }}
                  />
                </Box>
              )}

              {/* Imagem de capa lateral - 220px */}
              <Box sx={{
                width: 220,
                flexShrink: 0,
                borderRadius: '8px 0 0 8px',
                overflow: 'hidden',
                position: 'relative'
              }}>
                {collection.coverImage ? (
                  <img
                    src={collection.coverImage}
                    alt={collection.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://www.mensure.com.br/public/mensure_logo.jpg';
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.800'
                  }}>
                    <CollectionIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                  </Box>
                )}

                {/* Badge de tipo */}
                <Chip
                  icon={isTagBased ? <TagIcon sx={{ fontSize: '0.75rem !important' }} /> : <ArticleIcon sx={{ fontSize: '0.75rem !important' }} />}
                  label={isTagBased ? 'Tags' : 'Artigos'}
                  size="small"
                  color={isTagBased ? 'primary' : 'secondary'}
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 8,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 20,
                    '& .MuiChip-icon': { marginLeft: '4px', marginRight: '-2px' }
                  }}
                />
              </Box>

              {/* Separador vertical */}
              <Box sx={{ width: 2, bgcolor: 'divider', flexShrink: 0 }} />

              {/* Conteúdo */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
                {/* Área superior */}
                <Box sx={{ p: 1.5, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {/* Título e Data */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        flex: 1
                      }}
                    >
                      {collection.name || 'Sem nome'}
                    </Typography>
                    {createdAt && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '0.75rem', flexShrink: 0, whiteSpace: 'nowrap' }}
                      >
                        {createdAt}
                      </Typography>
                    )}
                  </Box>

                  {/* Descrição */}
                  {collection.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.813rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {collection.description}
                    </Typography>
                  )}
                </Box>

                {/* Footer com métricas + ações */}
                <Box sx={{
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.02)'
                    : 'rgba(0, 0, 0, 0.02)',
                  backdropFilter: 'none',
                  WebkitBackdropFilter: 'none',
                  px: 2,
                  py: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2
                }}>
                  {/* Métricas */}
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ArticleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                        {articleCount}
                      </Typography>
                    </Box>

                    {isTagBased && tagCount > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TagIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {tagCount}
                        </Typography>
                      </Box>
                    )}

                    {totalValuation > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AttachMoney sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatCurrency(totalValuation)}
                        </Typography>
                      </Box>
                    )}

                    {totalLikes > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ThumbUp sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatNumber(totalLikes)}
                        </Typography>
                      </Box>
                    )}

                    {totalViews > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Visibility sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                          {formatNumber(totalViews)}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Ações */}
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCollection?.(collection);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: 'action.hover' }
                      }}
                    >
                      <Edit sx={{ fontSize: '1rem' }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCollection?.(collection);
                      }}
                      sx={{
                        width: 28,
                        height: 28,
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main', bgcolor: 'action.hover' }
                      }}
                    >
                      <Delete sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // Renderização em grid (padrão)
  return (
    <Grid container spacing={1}>
      {collections.map((collection) => {
        const isSelected = selectedCollections.includes(collection._id);

        return (
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={collection._id || collection.id}>
            <Box sx={{ position: 'relative' }}>
              {selectionMode && (
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCollectionSelect?.(collection._id);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 10,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    '& .MuiSvgIcon-root': {
                      color: isSelected ? 'primary.main' : '#fff',
                    },
                  }}
                />
              )}
              <CollectionCoverCard
                collection={collection}
                onClick={onCollectionClick}
                onEdit={onEditCollection}
                onDelete={onDeleteCollection}
                isSelected={isSelected}
                selectionMode={selectionMode}
              />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CollectionCoverGrid;
